import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import dynamic from "next/dynamic";
import { useParams } from "next/navigation";
import { useState } from "react";
import BaseModule from "@/components/site-builder/BaseModule";
import { useClientConfig } from "@/context/clientConfig";
import { ExhibitorPageType } from "@/exhibitors/constants";
import EventEdition from "@/exhibitors/EventEdition";
import type { ExhibitorListModuleType } from "./types";
import { getPageType } from "./utils";

const ExhibitorListing = dynamic(() => import("./ExhibitorListing"), {
  ssr: false,
});
const ExhibitorProfile = dynamic(() => import("./ExhibitorProfile"), {
  ssr: false,
});
const ProductPage = dynamic(() => import("./ProductPage"), {
  ssr: false,
});
const ResourcePage = dynamic(() => import("./ResourcePage"), {
  ssr: false,
});

export interface ExhibitorListModuleProps {
  module: ExhibitorListModuleType;
}

export default function ExhibitorListModule(props: ExhibitorListModuleProps) {
  const { module } = props;
  const {
    body: { eventEditionCode },
  } = module;

  const {
    exhibitors: { host },
  } = useClientConfig();

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            async queryFn({ queryKey }) {
              const response = await fetch(`${host}${queryKey[0]}`);
              return await response.json();
            },
          },
        },
      }),
  );

  const params = useParams();
  const pageType = getPageType(params);

  if (pageType === ExhibitorPageType.Listing) {
    return (
      <BaseModule module={module}>
        <QueryClientProvider client={queryClient}>
          <EventEdition eventEditionCode={eventEditionCode}>
            <ExhibitorListing />
          </EventEdition>
          <ReactQueryDevtools />
        </QueryClientProvider>
      </BaseModule>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <EventEdition eventEditionCode={eventEditionCode}>
        {pageType === ExhibitorPageType.ExhibitorProfile && (
          <ExhibitorProfile />
        )}
        {pageType === ExhibitorPageType.ProductPage && <ProductPage />}
        {pageType === ExhibitorPageType.ResourcePage && <ResourcePage />}
      </EventEdition>
      <ReactQueryDevtools />
    </QueryClientProvider>
  );
}
